import React, { useMemo, useState } from "react";
import { Formik, Form, Field, ErrorMessage, useFormikContext } from "formik";
import * as Yup from "yup";
import { PriceInput } from "@/components/form";
import { KeenIcon } from "@/components";
import { useConfirmationModal } from '@/hooks';
import { ConfirmationModal } from '@/components/modal';

const safeNum = (n) => {
    if (n === null || n === undefined) return 0;
    const num = parseFloat(n);
    return Number.isNaN(num) ? 0 : num;
};

const currency = (n) =>
    `$${safeNum(n).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;

/**
 * Merge invoice items
 */
const mergeItems = (list) => {
    const time_entries = list.flatMap((i) =>
        (i?.time_entries || []).map((t) => ({ ...t, __invoice: i.number }))
    );
    const flat_fees = list.flatMap((i) =>
        (i?.flat_fees || []).map((t) => ({ ...t, __invoice: i.number }))
    );
    const expenses = list.flatMap((i) =>
        (i?.expenses || []).map((t) => ({ ...t, __invoice: i.number }))
    );
    return { time_entries, flat_fees, expenses };
};

/**
 * Compute summary strictly from invoice-level fields.
 */
const computeSummary = (list) => {
    const total = list.reduce((s, inv) => s + safeNum(inv?.total), 0);
    const total_discount = list.reduce(
        (s, inv) => s + safeNum(inv?.total_discount),
        0
    );
    const total_tax = list.reduce((s, inv) => s + safeNum(inv?.total_tax), 0);

    return { total, total_discount, total_tax };
};

// useEffect(() => {
//     if (shouldReset && elements) {
//       const cardElement = elements.getElement(CardElement);
//       if (cardElement) {
//         cardElement.clear();
//       }
//     }
//     if (shouldReset) {
//       setPaymentNote('')
//     }
//   }, [shouldReset, elements]);

const PaymentModal = ({
    invoiceData,
    refresh,
    onConfirm,
    onClose,
    open,
    loading,
}) => {
    const list = useMemo(
        () =>
            Array.isArray(invoiceData)
                ? invoiceData.filter(Boolean)
                : invoiceData
                    ? [invoiceData]
                    : [],
        [invoiceData]
    );
    console.log('list', list);

    const merged = useMemo(() => mergeItems(list), [list]);
    const summary = useMemo(() => computeSummary(list), [list]);
    const isSingle = list.length === 1;
    const single = isSingle ? list[0] : null;
    const isPartiallyPaid = isSingle && single?.payment_status === 'partially_paid';
    const { isOpen, actionId, openModal, closeModal } = useConfirmationModal();

    console.log(single);

    // Validation (cap at combined total)
    const maxPayable = isPartiallyPaid
        ? Math.max(summary.total - safeNum(single?.partial_payment_amount), 0)
        : summary.total;

    const SettlementSchema = useMemo(
        () =>
            Yup.object().shape({
                settlement_price: Yup.number()
                    .typeError("Settlement amount must be a number")
                    .positive("Settlement amount must be greater than zero")
                    .max(
                        maxPayable || 0,
                        `Settlement amount cannot exceed ${currency(maxPayable)}`
                    )
                    .required("Settlement amount is required"),
                payment_note: Yup.string().trim().required("Payment Note is required"),
            }),
        [maxPayable]
    );

    const initialSettlement =
        typeof single?.settlement_price !== "undefined"
            ? single.total
            : summary.total || "";
    const resolvedInitialSettlement = isPartiallyPaid ? maxPayable : initialSettlement;

    const renderItemList = (title, items) => (
        <div className="border-t border-primary-clarity border-dashed border-clarity my-4 pt-2">
            <h3 className="font-medium mb-2">{title}</h3>
            {items.length === 0 && (
                <p className="text-xs text-center">No {title}</p>
            )}
            {items.map((it) => (
                <div
                    key={`${title}-${it.id}-${it.__invoice}`}
                    className="text-sm mb-2 border-b border-dashed pb-1.5"
                >
                    <div className="flex items-center justify-between mb-1 text-xs">
                        <div className="flex items-center gap-2">
                            <span>Date:</span> {it.date}
                            {!isSingle && (
                                <span className="text-[10px] badge badge-xs badge-outline">
                                    #{it.__invoice}
                                </span>
                            )}
                        </div>
                        {currency(it.rate)}
                    </div>
                    <div className="flex items-center justify-between mb-1 text-xs">
                        <div className="flex items-center gap-1.5">
                            Qty
                            {/* bg-dark text-white rounded-full w-4.5 h-4.5  */}
                            {it.quantity > 1 && (
                                <span className="text-[9px] font-semibold badge badge-xs flex items-center justify-center">
                                    x{it.quantity}
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center justify-between">
                        <div>Total:</div> {currency(it.total)}
                    </div>
                </div>
            ))}
        </div>
    );

    const handleProceedAfterWarning = () => {
        closeModal()
        onConfirm(actionId)
    }

    // Half-cent tolerance so a value that only differs by float noise isn't treated
    // as a shortfall and made to answer a question that doesn't apply.
    const isShortPayment = (amount) =>
        amount !== '' && amount !== null && safeNum(amount) < maxPayable - 0.005;

    const shortfallFor = (amount) => Math.max(maxPayable - safeNum(amount), 0);

    // `is_settled` decides the server branch: true writes off the balance and closes
    // the invoice, false records a part payment. It is only asked when the amount is
    // short — paying the full balance settles nothing, so keep the prior behaviour
    // there (single invoices take the payment path, multiples the settlement path,
    // and with no shortfall both land on fully paid).
    const resolvePayload = (values) => ({
        ...values,
        is_settled: isShortPayment(values.settlement_price)
            ? Boolean(values.is_settled)
            : !isSingle,
    });

    return (
        <>
            <div
                className={`bg-black/50 fixed inset-0 z-50 flex justify-end transition-opacity duration-300 ${open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
                    }`}
            >
                <div
                    className={`relative z-10 h-full w-4/5 max-w-[28rem] bg-[--tw-navbar-bg] dark:bg-[--tw-navbar-bg-dark]
                    border border-gray-300 dark:border-t-light dark:border-light p-6 shadow-lg rounded-s-lg
                    transform transition-transform duration-500 ease-in-out
                    ${open ? "translate-x-0" : "translate-x-full"}`}
                >
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-lg font-semibold">
                            {isSingle
                                ? `Pay Invoice #${single?.number || ""}`
                                : `Pay ${list.length} Invoices`}
                        </h2>
                        <button onClick={onClose} className="modal-close-btn">
                            <KeenIcon icon="cross" />
                        </button>
                    </div>

                    <div className="overflow-y-auto max-h-[calc(100vh-27rem)] pr-2 mb-5 custom-scrollbar">
                        {/* Item lists */}
                        {renderItemList("Time Entries", merged.time_entries)}
                        {renderItemList("Flat Fees", merged.flat_fees)}
                        {renderItemList("Expenses", merged.expenses)}
                    </div>

                    {/* Totals */}
                    <div className="border-t border-primary-clarity border-dashed border-clarity mt-4 pt-3 space-y-1 text-sm">
                        <div className="flex items-center justify-between mb-1">
                            <div>Discount:</div>
                            -{currency(summary.total_discount)}
                        </div>
                        <div className="flex items-center justify-between">
                            <div>Tax:</div> +{currency(summary.total_tax)}
                        </div>
                        <div className="flex items-center justify-between text-base font-medium text-success">
                            <div>Total:</div> {currency(summary.total)}
                        </div>
                        {isPartiallyPaid && (
                            <>
                                <div className="flex items-center justify-between mb-1">
                                    <div>Paid:</div> -{currency(safeNum(single?.partial_payment_amount))}
                                </div>
                                <div className="flex items-center justify-between mb-1 text-warning">
                                    <div>Remaining:</div> {currency(maxPayable)}
                                </div>
                            </>
                        )}
                    </div>
                    {/* Settlement Form */}
                    <Formik
                        enableReinitialize
                        initialValues={{
                            settlement_price: resolvedInitialSettlement,
                            payment_note: '',
                            // Deliberately undecided. When the amount entered is short of
                            // what's owed the two outcomes differ a lot and one of them
                            // can't be undone, so the user picks rather than inheriting a
                            // default. Resolved in resolvePayload for the exact-amount case.
                            is_settled: null
                        }}
                        validationSchema={SettlementSchema}
                        onSubmit={(values, { resetForm }) => { onConfirm(resolvePayload(values), resetForm) }}
                    >
                        {({ isSubmitting, values, handleSubmit, setFieldValue }) => {
                            const short = isShortPayment(values.settlement_price);
                            const shortfall = shortfallFor(values.settlement_price);
                            const needsChoice = short && values.is_settled === null;

                            return (
                            <Form className="flex flex-col gap-3 mt-4">
                                <div>
                                    {short && (
                                        <div className="mb-3 rounded-md border border-warning bg-warning-light p-3">
                                            <p className="text-xs font-medium mb-0.5">
                                                That is {currency(shortfall)} less than the {currency(maxPayable)} due.
                                            </p>
                                            <p className="text-[11px] leading-[1.4em] text-gray-700 mb-2.5">
                                                How should the remaining {currency(shortfall)} be handled?
                                            </p>

                                            <label className="flex items-start gap-2 mb-2 cursor-pointer">
                                                <input
                                                    type="radio"
                                                    className="radio radio-sm mt-0.5 shrink-0"
                                                    checked={values.is_settled === false}
                                                    onChange={() => setFieldValue('is_settled', false)}
                                                />
                                                <span className="text-[11px] leading-[1.4em]">
                                                    <span className="block font-medium text-gray-900">Record as a partial payment</span>
                                                    <span className="text-gray-700">
                                                        {isSingle ? 'The invoice stays' : 'The invoices stay'} open with {currency(shortfall)} still due.
                                                    </span>
                                                </span>
                                            </label>

                                            <label className="flex items-start gap-2 cursor-pointer">
                                                <input
                                                    type="radio"
                                                    className="radio radio-sm mt-0.5 shrink-0"
                                                    checked={values.is_settled === true}
                                                    onChange={() => setFieldValue('is_settled', true)}
                                                />
                                                <span className="text-[11px] leading-[1.4em]">
                                                    <span className="block font-medium text-gray-900">Settle in full for this amount</span>
                                                    <span className="text-gray-700">
                                                        {isSingle ? 'The invoice closes' : 'The invoices close'} as fully paid and {currency(shortfall)} is written off as a settlement discount. This cannot be undone.
                                                    </span>
                                                </span>
                                            </label>
                                        </div>
                                    )}
                                    <div className="flex items-center justify-between mb-1">
                                        <label htmlFor="settlement_price" className="w-fit form-label">
                                            Pay Amount
                                        </label>
                                        {Number(values.settlement_price) !== maxPayable && (
                                            <div className="text-[11px] text-warning leading-none">
                                                Max Payable: {currency(maxPayable)}
                                                {/* Max payable: {currency(summary.total)} */}
                                            </div>
                                        )}
                                    </div>
                                    {/* <ErrorMessage
                                    name="settlement_price"
                                    component="div"
                                    className="text-red-500 text-[11px] leading-[1.5em]"
                                /> */}

                                    <PriceInput
                                        name="settlement_price"
                                        prefix="$"
                                        className="input"
                                    />

                                    <div className="mt-3">
                                        <label className="form-label mb-1">Payment Note <span className="pl-px text-red-600">*</span></label>
                                        <Field
                                            name="payment_note"
                                            as="textarea"
                                            placeholder="Add a payment note here"
                                            rows="2"
                                            className="min-h-[60px] input py-3 leading-[1.4em]"
                                        />
                                        <ErrorMessage
                                            name="payment_note"
                                            component="div"
                                            className="text-red-500 text-[11px] leading-[1.5em]"
                                        />
                                    </div>
                                </div>

                                <div className="flex justify-end gap-2">
                                    <button
                                        type="button"
                                        disabled={
                                            loading ||
                                            isSubmitting ||
                                            values.settlement_price == '' ||
                                            Number(values.settlement_price) > maxPayable ||
                                            needsChoice
                                        }
                                        className="w-full bg-primary text-sm text-white py-2 rounded disabled:opacity-50"
                                        onClick={() => {
                                            // Only the settlement needs a second look: it writes off the
                                            // shortfall and can't be reversed. A partial payment is additive
                                            // and was just chosen explicitly, so it goes straight through.
                                            const settling = short && values.is_settled === true;
                                            if (settling && values.payment_note.trim() !== '') {
                                                openModal(resolvePayload(values));
                                            } else {
                                                handleSubmit();
                                            }
                                        }}
                                    >
                                        {needsChoice
                                            ? "Choose how to handle the balance"
                                            : loading || isSubmitting ? "Please Wait..." : "Proceed Payment"}
                                    </button>
                                    {/* <button
                                    type="button"
                                    onClick={onClose}
                                    className="btn btn-sm btn-danger btn-outline"
                                >
                                    Cancel
                                </button> */}
                                </div>
                            </Form>
                            );
                        }}
                    </Formik>
                </div>
            </div>
            <ConfirmationModal
                show={isOpen}
                onClose={closeModal}
                onConfirm={handleProceedAfterWarning}
                // message={`Are you sure you want to pay $${actionId?.settlement_price} for the selected ${isSingle ? 'invoice' : 'invoices'}? This action is permanent and cannot be undone.`}
                message={`${currency(shortfallFor(actionId?.settlement_price))} will be written off as a settlement discount and ${isSingle ? 'the invoice will be closed' : 'the selected invoices will be closed'} as fully paid. This cannot be undone.`}
                title="Are You Sure?"
                showTitle={false}
                btnText="Proceed"
            // customBtnClass='btn-warning'
            />
            {/* ${currency(values.settlement_price)} */}
        </>
    );
};

export { PaymentModal };
